import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import { useOptibotStore } from '../stores/optibot-store';
import { sessionLabel } from '../hooks/useDeviceSessionStream';
import { useDeviceTroubleshootSessions } from '../hooks/useDeviceTroubleshootHistory';
import { DeviceTroubleshootSessionList } from './DeviceTroubleshootSessionList';

/** Troubleshoot-session history inside the OptiBot panel — the durable, chat-independent entry point.
 *  A row opens that session into the FocusedSessionPane as a read-only transcript replay (history mode).
 *  (Re-activating a session = the resume flow in DeviceTroubleshootHistoryPanel; not offered here yet.) */
export function OptiBotSessionHistory() {
  const { t } = useTranslation();
  const { sessions, loading, error } = useDeviceTroubleshootSessions();
  const openSession = useOptibotStore((s) => s.openSession);

  // Customer-facing surface: hide staff-opened sessions (cross-tenant support runs those from the staff
  // console, not the customer's OptiBot panel).
  const customerSessions = sessions.filter((s) => s.mode !== 'staff');

  const select = (sessionId: string) => {
    const row = customerSessions.find((s) => s.sessionId === sessionId);
    openSession({
      // History replay needs no live token — FocusedSessionPane fetches the transcript by sessionId.
      session: { sessionId, sessionToken: '', streamUrl: '', actor: row?.actor },
      mode: 'history',
      deviceLabel: row ? sessionLabel({ deviceName: row.deviceName, title: row.title }) : undefined,
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2 text-sm font-medium">
        <History className="h-4 w-4 text-muted-foreground" />
        {t('optibot.session.history.title')}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="p-4 text-xs text-destructive">
            {t('optibot.session.history.loadError')}
          </div>
        ) : (
          <DeviceTroubleshootSessionList
            sessions={customerSessions}
            onSelect={select}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}
